import { FC, Fragment, ReactNode } from 'react'

import { Cluster, Grid, Separator, Skeleton, Stack, Typography } from '@forto-dev/phoenix-ui-core'
import { Layout } from '@forto-dev/phoenix-ui-core/ship'

import { Footer } from '@/fragments/footer'
import { Header } from '@/fragments/header'
import { Navigation } from '@/fragments/navigation'
import { useLocalisation } from '@/utils/localisation'

import { DashboardKpiCardSkeleton } from './dashboardKpiCard'
import { DASHBOARD_LAYOUT } from './dashboardLayout'
import { DashboardSectionHeadingSkeleton } from './dashboardSection'
import { DashboardWidgetCardSkeleton } from './dashboardWidgetCard'

export const DashboardKpiStripSkeleton: FC = () => (
  <Grid {...DASHBOARD_LAYOUT.kpiGrid}>
    {Array.from({ length: DASHBOARD_LAYOUT.kpiCount }).map((_, index) => (
      <DashboardKpiCardSkeleton key={index} />
    ))}
  </Grid>
)

export const DashboardSectionSkeleton: FC<{ columns: 1 | 2 | 3 }> = ({ columns }) => (
  <Stack gap={16}>
    <DashboardSectionHeadingSkeleton />
    <Grid col={columns} {...DASHBOARD_LAYOUT.sectionGrid}>
      {Array.from({ length: columns }).map((_, index) => (
        <DashboardWidgetCardSkeleton key={index} withTitle />
      ))}
    </Grid>
  </Stack>
)

type DashboardRecentActivityViewportProps = {
  scrollable: boolean
  children: ReactNode
}

export const DashboardRecentActivityViewport: FC<DashboardRecentActivityViewportProps> = ({ scrollable, children }) => (
  // eslint-disable-next-line react/forbid-dom-props
  <div className={scrollable ? 'max-h-64 overflow-y-auto' : 'max-h-64 overflow-hidden'}>{children}</div>
)

export const DashboardRecentActivityListSkeleton: FC = () => (
  <DashboardRecentActivityViewport scrollable={false}>
    <Stack gap={12}>
      {Array.from({ length: DASHBOARD_LAYOUT.recentActivityVisibleRows }).map((_, index) => (
        <Fragment key={index}>
          <Cluster gap={8} y="center" fullWidth>
            <Skeleton variant="avatar" />
            <Stack gap={2} fullWidth>
              <Skeleton variant="content" />
              <Skeleton variant="badge" fullWidth />
            </Stack>
            <Skeleton variant="badge" />
          </Cluster>
          {index < DASHBOARD_LAYOUT.recentActivityVisibleRows - 1 ? <Separator orientation="horizontal" /> : null}
        </Fragment>
      ))}
    </Stack>
  </DashboardRecentActivityViewport>
)

export const DashboardOperationsSkeleton: FC = () => (
  <Stack gap={16}>
    <DashboardSectionHeadingSkeleton />
    <Grid col={2} {...DASHBOARD_LAYOUT.sectionGrid}>
      <DashboardWidgetCardSkeleton withTitle>
        <DashboardRecentActivityListSkeleton />
      </DashboardWidgetCardSkeleton>
      <DashboardWidgetCardSkeleton withTitle />
    </Grid>
  </Stack>
)

export const DashboardMainSkeleton: FC = () => (
  <Stack gap={24}>
    <DashboardKpiStripSkeleton />
    <DashboardOperationsSkeleton />
  </Stack>
)

export const DashboardSkeleton: FC = () => {
  const { translate } = useLocalisation()

  return (
    <Layout
      header={<Header />}
      navigation={<Navigation />}
      title={<Typography variant="h4">{translate('navigation.dashboard')}</Typography>}
      main={<DashboardMainSkeleton />}
      footer={<Footer />}
    />
  )
}
